import { useEffect, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router';
import { LOGIN_PATH } from '../config';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { restoreSession } from '../store/authSlice';
import { FullPageProgress } from '../components/FullPageProgress';

// Wraps everything that requires a session. Every route in the app is behind
// this except /login and the callback — putting either behind it would be a
// redirect loop.
export const RequireAuth = ({ children }: { children: ReactNode }) => {
  const dispatch = useAppDispatch();
  const status = useAppSelector((state) => state.auth.status);
  const location = useLocation();

  // Asks once, on first render of any protected route, whether a session
  // already exists in sessionStorage. Without this a page reload would look
  // exactly like a first visit.
  useEffect(() => {
    if (status === 'unknown') {
      void dispatch(restoreSession());
    }
  }, [status, dispatch]);

  // `unknown` and `pending` both mean "we do not know yet", and they must not
  // fall through to the redirect below. Treating them as unauthenticated is the
  // bug where reloading a protected page bounces you through login every time.
  if (status === 'unknown' || status === 'pending') {
    return <FullPageProgress label="Checking your session…" />;
  }

  if (status === 'unauthenticated') {
    // Carried in router state, then handed to the provider by the login page so
    // it survives the round trip and comes back on the callback.
    return (
      <Navigate
        to={LOGIN_PATH}
        state={{ returnTo: `${location.pathname}${location.search}` }}
        replace
      />
    );
  }

  return children;
};
